import { Order, SystemConfig, TimingGuidance, Driver, SafeDispatchAnalysis, SafeDispatchSequencedStop, ServiceTimeResolution } from '../../../types';
import { calculateServiceTimeMetrics } from '../fleet/telemetry';
import {
    COLD_START_MINS_PER_KM,
    speedKmhToMinPerKm,
    MIN_LEARNED_SPEED_KMH,
    MAX_LEARNED_SPEED_KMH,
    DEFAULT_SERVICE_TIME_OWN,
    DEFAULT_SERVICE_TIME_THIRD,
    DEFAULT_SLA_TOLERANCE_OWN,
    DEFAULT_SLA_TOLERANCE_THIRD
} from '../constants';
import { recordEstimateUsage } from '../metrics';

const DRAINAGE_THRESHOLD_MINUTES = 25;

const FALLBACK_EXTREME_TRAVEL_MINUTES = 15;
const SERVICE_TIME_CONFIDENCE_THRESHOLD = 0.65;

const resolveProfile = (config: SystemConfig, isOwnFleet: boolean) => (
    isOwnFleet ? config.fleetConfig : config.thirdPartyConfig
);

const resolveFallbackTravelTime = (
    order: Order,
    isOwnFleet: boolean,
    driver?: Driver
): number => {
    let minsPerKm = COLD_START_MINS_PER_KM;
    let usedFallback = true;
    let speedKmh: number | undefined;

    if (driver && isOwnFleet) {
        const learnedSpeed = driver.avgSpeedKmH;
        if (learnedSpeed && learnedSpeed >= MIN_LEARNED_SPEED_KMH && learnedSpeed <= MAX_LEARNED_SPEED_KMH) {
            minsPerKm = speedKmhToMinPerKm(learnedSpeed);
            usedFallback = false;
            speedKmh = learnedSpeed;
        }
    }

    recordEstimateUsage({ source: 'timing', usedFallback, speedKmh });

    const travelTime = (order.distance || 0) * minsPerKm;
    return order.distance ? travelTime : FALLBACK_EXTREME_TRAVEL_MINUTES;
};

const resolveTravelTime = (
    order: Order,
    cumulativeTravelMinutes: number,
    isOwnFleet: boolean,
    driver?: Driver
): number => {
    if (cumulativeTravelMinutes > 0) return cumulativeTravelMinutes;
    if (order.estimatedDuration && order.estimatedDuration > 0) return order.estimatedDuration;
    return resolveFallbackTravelTime(order, isOwnFleet, driver);
};

const resolveServiceTime = (
    config: SystemConfig,
    isOwnFleet: boolean,
    allOrdersForLearning: Order[],
    driver?: Driver
): ServiceTimeResolution => {
    const profile = resolveProfile(config, isOwnFleet);
    const profileServiceTime = profile?.serviceTimeMinutes || (isOwnFleet ? DEFAULT_SERVICE_TIME_OWN : DEFAULT_SERVICE_TIME_THIRD);

    if (!isOwnFleet) {
        return {
            minutes: profileServiceTime,
            source: 'profile_default_fallback',
            confidence: 1,
            sampleCount: 0,
            rationale: 'Third-party profile keeps configured service-time baseline.'
        };
    }

    const fleetMetrics = calculateServiceTimeMetrics(allOrdersForLearning);
    const driverMetrics = driver
        ? calculateServiceTimeMetrics(allOrdersForLearning, { driverId: driver.id })
        : null;

    if (
        driverMetrics &&
        !driverMetrics.isStale &&
        driverMetrics.confidence >= SERVICE_TIME_CONFIDENCE_THRESHOLD
    ) {
        const minutes = driverMetrics.last30Days > 0 ? driverMetrics.last30Days : driverMetrics.history;
        return {
            minutes,
            source: 'driver_real_data',
            confidence: driverMetrics.confidence,
            sampleCount: driverMetrics.sampleCount,
            lastObservationAt: driverMetrics.lastObservationAt,
            rationale: 'Using rider-specific observations with sufficient confidence.'
        };
    }

    if (
        fleetMetrics.confidence >= SERVICE_TIME_CONFIDENCE_THRESHOLD &&
        !fleetMetrics.isStale
    ) {
        const minutes = fleetMetrics.last30Days > 0 ? fleetMetrics.last30Days : fleetMetrics.history;
        return {
            minutes,
            source: 'fleet_recent_data',
            confidence: fleetMetrics.confidence,
            sampleCount: fleetMetrics.sampleCount,
            lastObservationAt: fleetMetrics.lastObservationAt,
            rationale: 'Using own-fleet recent history while rider-specific data is sparse.'
        };
    }

    if (driver?.avgServiceTimeMinutes && driver.avgServiceTimeMinutes > 0) {
        return {
            minutes: driver.avgServiceTimeMinutes,
            source: 'driver_profile_fallback',
            confidence: driverMetrics?.confidence ?? 0,
            sampleCount: driverMetrics?.sampleCount ?? 0,
            lastObservationAt: driverMetrics?.lastObservationAt,
            rationale: 'Using persisted rider profile fallback because observations are sparse or stale.'
        };
    }

    return {
        minutes: profileServiceTime,
        source: 'profile_default_fallback',
        confidence: fleetMetrics.confidence,
        sampleCount: fleetMetrics.sampleCount,
        lastObservationAt: fleetMetrics.lastObservationAt,
        rationale: 'Fallback to configured profile due to insufficient fresh learning history.'
    };
};

const resolveTolerance = (
    config: SystemConfig,
    isOwnFleet: boolean,
    routeSize: number
): number => {
    const profile = resolveProfile(config, isOwnFleet);
    const baseTolerance = profile?.slaToleranceMinutes || (isOwnFleet ? DEFAULT_SLA_TOLERANCE_OWN : DEFAULT_SLA_TOLERANCE_THIRD);
    return routeSize === 1 ? 0 : baseTolerance;
};

export type CanonicalSlaSlackScoringInput = {
    minSlackMinutes: number;
    worstOrderId?: string;
    stopCount: number;
    slackByOrderId: Record<string, number>;
};

export const buildCanonicalSlaSlackScoringInput = (
    stops: SafeDispatchSequencedStop[]
): CanonicalSlaSlackScoringInput => {
    if (stops.length === 0) {
        return {
            minSlackMinutes: Number.POSITIVE_INFINITY,
            stopCount: 0,
            slackByOrderId: {}
        };
    }

    let minSlackMinutes = Number.POSITIVE_INFINITY;
    let worstOrderId: string | undefined;
    const slackByOrderId: Record<string, number> = {};

    stops.forEach((stop) => {
        const slackValue = stop.analysis.value;
        slackByOrderId[stop.orderId] = slackValue;

        if (slackValue < minSlackMinutes) {
            minSlackMinutes = slackValue;
            worstOrderId = stop.orderId;
        }
    });

    return {
        minSlackMinutes,
        worstOrderId,
        stopCount: stops.length,
        slackByOrderId
    };
};

const getMinSlackFromStops = (stops: SafeDispatchSequencedStop[]): number => {
    return buildCanonicalSlaSlackScoringInput(stops).minSlackMinutes;
};

/**
 * v45.3: analyzeSafeDispatch - High-Fidelity Performance
 * - Aplica rigorosamente os parâmetros independentes de Frota Própria e Apps Externos.
 * - Centraliza o cálculo de SLA para evitar discrepâncias entre o 'Cérebro' e a 'UI'.
 */
export const analyzeSafeDispatch = (
    order: Order,
    config: SystemConfig,
    cumulativeTravelMinutes: number = 0,
    isOwnFleet: boolean = true,
    allOrdersForLearning: Order[] = [],
    driver?: Driver,
    routeSize: number = 1 // v56.1: Centralized tolerance logic (1 = single order, no tolerance)
): SafeDispatchAnalysis => {
    const now = Date.now();
    const elapsedMins = order.createdAt ? (now - order.createdAt) / 60000 : 0;
    const deadline = order.timeLimit || 45;

    // 1. Definição da Velocidade e Viagem
    const travelTime = resolveTravelTime(order, cumulativeTravelMinutes, isOwnFleet, driver);

    // 2. Parâmetros Independentes por Perfil
    const serviceTimeResolution = resolveServiceTime(config, isOwnFleet, allOrdersForLearning, driver);
    const serviceTime = serviceTimeResolution.minutes;

    // Tolerância SLA (Margem de segurança)
    // v56.1: Single Order = Zero Tolerance. Multi-Order = Full Tolerance.
    const tolerance = resolveTolerance(config, isOwnFleet, routeSize);

    // 3. Tempo restante de produção (apenas para retorno, NÃO para cálculo de saída)
    // O tempo de produção é paralelo ao deslocamento - a cozinha prepara ENQUANTO o entregador se desloca
    const kitchenRemaining = (!order.isReady && order.status !== 'ready')
        ? Math.max(0, (config.averageKitchenTime || 20) - elapsedMins)
        : 0;

    // Formula: Saída Segura = Deadline - Elapsed - Travel - Service + Tolerance
    // NÃO descontamos kitchenRemaining pois a produção acontece em paralelo ao deslocamento
    const value = Math.floor(deadline - elapsedMins - travelTime - serviceTime + tolerance);

    return {
        value,
        deadline,
        elapsed: elapsedMins,
        travel: travelTime,
        kitchen: kitchenRemaining,
        service: serviceTime,
        tolerance,
        serviceSource: serviceTimeResolution.source,
        serviceConfidence: serviceTimeResolution.confidence,
        serviceSampleCount: serviceTimeResolution.sampleCount,
        serviceLastObservationAt: serviceTimeResolution.lastObservationAt,
        serviceRationale: serviceTimeResolution.rationale
    };
};

/**
 * v58.0: Sequenciamento unificado de saída segura.
 * Cada parada considera: viagem acumulada + serviços anteriores.
 */
export const calculateSafeDispatchSequence = (
    routeOrders: Order[],
    config: SystemConfig,
    isOwnFleet: boolean,
    allOrdersForLearning: Order[] = [],
    driver?: Driver,
    travelMinutesByOrderId: Record<string, number> = {}
): SafeDispatchSequencedStop[] => {
    const routeSize = Math.max(1, routeOrders.length);
    let cumulativeTravelMinutes = 0;
    let cumulativeServiceMinutes = 0;

    return routeOrders.map((order, stopIndex) => {
        const mappedLegTravel = travelMinutesByOrderId[order.id];
        const mappedLegTravelValue = mappedLegTravel ?? 0;
        const hasMappedLegTravel = Number.isFinite(mappedLegTravelValue) && mappedLegTravelValue > 0;
        const legTravelMinutes = hasMappedLegTravel
            ? mappedLegTravelValue
            : (order.estimatedDuration && order.estimatedDuration > 0
                ? order.estimatedDuration
                : resolveFallbackTravelTime(order, isOwnFleet, driver));

        cumulativeTravelMinutes += legTravelMinutes;

        const analysis = analyzeSafeDispatch(
            order,
            config,
            cumulativeTravelMinutes,
            isOwnFleet,
            allOrdersForLearning,
            driver,
            routeSize
        );

        cumulativeServiceMinutes += analysis.service;

        return {
            orderId: order.id,
            stopIndex,
            order,
            legTravelMinutes,
            cumulativeTravelMinutes,
            cumulativeServiceMinutes,
            cumulativeRouteMinutes: cumulativeTravelMinutes + cumulativeServiceMinutes,
            analysis
        };
    });
};

export const calculateSafeDispatchCountdown = (
    order: Order,
    config: SystemConfig,
    cumulativeTravelMinutes: number = 0,
    isOwnFleet: boolean = true,
    allOrdersForLearning: Order[] = [],
    driver?: Driver
): number => {
    return analyzeSafeDispatch(order, config, cumulativeTravelMinutes, isOwnFleet, allOrdersForLearning, driver).value;
};

export const getTimingGuidance = (
    routeOrders: Order[],
    config: SystemConfig,
    isOwnFleet: boolean,
    driverReturnDelay: number = 0,
    isCostEfficientToHold: boolean = false,
    allOrdersForLearning: Order[] = [],
    driver?: Driver
): TimingGuidance => {
    const allReady = routeOrders.every(o => o.status === 'ready' || o.isReady);
    const stops = calculateSafeDispatchSequence(routeOrders, config, isOwnFleet, allOrdersForLearning, driver);
    const minSlack = getMinSlackFromStops(stops);
    return processTimingLogic(allReady, minSlack, driverReturnDelay, isCostEfficientToHold, config);
};

export const getTimingGuidanceWithTravelTimes = (
    routeOrders: Order[],
    config: SystemConfig,
    isOwnFleet: boolean,
    driverReturnDelay: number = 0,
    travelMinutesByOrderId: Record<string, number>,
    isCostEfficientToHold: boolean = false,
    allOrdersForLearning: Order[] = [],
    driver?: Driver
): TimingGuidance => {
    const allReady = routeOrders.every(o => o.status === 'ready' || o.isReady);
    const stops = calculateSafeDispatchSequence(routeOrders, config, isOwnFleet, allOrdersForLearning, driver, travelMinutesByOrderId);
    const minSlack = getMinSlackFromStops(stops);
    return processTimingLogic(allReady, minSlack, driverReturnDelay, isCostEfficientToHold, config);
};

const processTimingLogic = (allReady: boolean, minSlack: number, driverReturnDelay: number, isCostEfficientToHold: boolean, config: SystemConfig): TimingGuidance => {
    const minsToClose = getMinutesUntilClose(config);
    const isDrainageActive = minsToClose < (config.drainageThresholdMinutes ?? DRAINAGE_THRESHOLD_MINUTES);
    const effectiveSlack = minSlack - driverReturnDelay;
    if (!allReady) return { status: 'waiting_kitchen', label: 'AGUARDANDO COZINHA', reason: 'Produção em andamento. Residual calculado automaticamente.' };
    if (isDrainageActive) return { status: 'critical', label: 'DRENAGEM ATIVA', reason: 'Fechamento próximo. Despachando tudo que está pronto.' };
    if (driverReturnDelay > 0 && effectiveSlack > 0) return { status: 'waiting_batch', label: 'BUMERANGUE', waitMinutes: Math.ceil(driverReturnDelay), reason: `Aguardando retorno da frota (${Math.ceil(driverReturnDelay)}m).` };
    if (effectiveSlack < 5) return { status: 'critical', label: 'SAÍDA CRÍTICA', reason: 'Janela de despacho esgotando (SLA + Viagem + Serviço).' };
    if (isCostEfficientToHold && effectiveSlack > 10) return { status: 'waiting_batch', label: 'DESPACHO RETIDO', waitMinutes: 8, reason: 'Pedido pronto, mas segurado para maximizar ocupação da bag.' };
    return { status: 'optimal', label: 'JANELA ÓTIMA', reason: 'Momento ideal de despacho sem risco de atraso.' };
};

type OperatingHourRange = {
    start: string;
    end: string;
};

const toOperatingRanges = (value: unknown): OperatingHourRange[] => {
    if (!Array.isArray(value)) return [];
    return value.filter((range): range is OperatingHourRange => {
        if (!range || typeof range !== 'object') return false;
        const candidate = range as Record<string, unknown>;
        return typeof candidate.start === 'string' && typeof candidate.end === 'string';
    });
};

export const getMinutesUntilClose = (config: SystemConfig): number => {
    const now = new Date();
    const day = now.getDay();
    const currentMinutes = now.getHours() * 60 + now.getMinutes();

    // 1. Check if we are CURRENTLY OPEN (Inside a shift)
    const ranges = toOperatingRanges(config.operatingHours?.[day]);
    let minDiff = 9999;

    if (ranges.length > 0) {
        ranges.forEach((r) => {
            const [startH, startM] = r.start.split(':').map(Number);
            const [endH, endM] = r.end.split(':').map(Number);
            const startMins = startH * 60 + startM;
            let endMins = endH * 60 + endM;

            if (endMins < startMins) endMins += 24 * 60; // Handle midnight crossing

            let adjustedCurrent = currentMinutes;
            // Adjust current time if checking a late-night shift that spans midnight
            if (endMins > 24 * 60 && currentMinutes < startMins) adjustedCurrent += 24 * 60;

            if (adjustedCurrent >= startMins && adjustedCurrent <= endMins) {
                const diff = endMins - adjustedCurrent;
                if (diff < minDiff) minDiff = diff;
            }
        });
    }

    // If we are open, return the time remaining.
    if (minDiff !== 9999) return minDiff;

    // 2. If CLOSED, check if we are in POST-CLOSE DRAINAGE (Buffer Window)
    // The user requested that drainage continues after close, but NOT until the next day.
    // We define a reasonable "Cleanup Window" (e.g., 90 minutes) after closing.
    const POST_CLOSE_DRAINAGE_WINDOW = 90;
    let inPostCloseDrainage = false;

    // Check Today's ended shifts
    if (ranges.length > 0) {
        ranges.forEach((r) => {
            const [endH, endM] = r.end.split(':').map(Number);
            const endMins = endH * 60 + endM;
            // Only consider shifts that end TODAY (not crossing midnight into tomorrow, handled below)
            // or simple comparisons.
            // If currentMinutes is AFTER endMins and within window.
            if (currentMinutes > endMins && (currentMinutes - endMins) <= POST_CLOSE_DRAINAGE_WINDOW) {
                inPostCloseDrainage = true;
            }
        });
    }

    if (!inPostCloseDrainage) {
        // Check Yesterday's shifts (Crucial for late night closures, e.g. closed at 02:00, now it's 02:30)
        const prevDay = (day + 6) % 7;
        const prevRanges = toOperatingRanges(config.operatingHours?.[prevDay]);
        if (prevRanges.length > 0) {
            prevRanges.forEach((r) => {
                const [startH, startM] = r.start.split(':').map(Number);
                const [endH, endM] = r.end.split(':').map(Number);
                let startMins = startH * 60 + startM;
                let endMins = endH * 60 + endM;

                // Handle shifts crossing midnight (e.g. 18:00 - 02:00)
                // In context of Yesterday, 02:00 is 26:00 (24*60 + 120).
                if (endMins < startMins) endMins += 24 * 60;

                // Current time relative to Yesterday is (24*60 + currentMinutes)
                const currentRelative = (24 * 60) + currentMinutes;

                if (currentRelative > endMins && (currentRelative - endMins) <= POST_CLOSE_DRAINAGE_WINDOW) {
                    inPostCloseDrainage = true;
                }
            });
        }
    }

    // Return 0 to force Drainage Mode if in post-close window.
    // Return 9999 if genuinely closed (Pre-Open or Long Post-Close), disabling Drainage.
    return inPostCloseDrainage ? 0 : 9999;
};


export const calculateOrderSlack = (order: Order, config: SystemConfig, driver?: Driver): number => calculateSafeDispatchCountdown(order, config, 0, true, [], driver);

/**
 * v46.9: Capacity Preservation Protocol
 * Calculates the time window until the effective logistic peak.
 */
export const calculateSafeReturnWindow = (
    oracle: import('../../../types').OracleState,
    kitchen: import('../../../types').KitchenMetrics
): number => {
    const now = Date.now();
    const effectivePrepOffset = kitchen.effectivePrepTime * 1.1; // 10% safety buffer
    const predictedPeak = oracle.predictedPeakTime;

    // Safety check for past peaks
    if (predictedPeak < now) return 999;

    // Safe Window = (Peak + Prep) - Now
    const safeWindowMinutes = ((predictedPeak + (effectivePrepOffset * 60000)) - now) / 60000;

    return Math.max(0, safeWindowMinutes);
};

export const isRouteRiskyForPeak = (
    roundTripMinutes: number,
    safeWindowMinutes: number
): boolean => {
    // If we have plenty of time (e.g. > 1 hour), it's never risky
    if (safeWindowMinutes > 60) return false;

    // Strict check: Will the driver return *after* the safety window closes?
    return roundTripMinutes > safeWindowMinutes;
};

/**
 * v56.5: Converte minutos a partir de agora em horário absoluto (HH:mm).
 * Usado para exibir ETAs no formato de relógio para o despachante.
 */
export const formatETA = (minutesFromNow: number): string => {
    const now = new Date();
    const eta = new Date(now.getTime() + minutesFromNow * 60000);
    const hours = eta.getHours().toString().padStart(2, '0');
    const mins = eta.getMinutes().toString().padStart(2, '0');
    return `${hours}:${mins}`;
};
